import React, { useEffect, useState } from "react";
import { WifiOff, RefreshCw } from "lucide-react";

/**
 * Top-of-page banner shown when the app is serving cached data because the
 * network is unreachable.
 *
 * Detection sources
 *   - `navigator.onLine === false` at mount
 *   - `offline` / `online` window events (browser signals)
 *   - Custom "offline-fallback" event dispatched by our axios interceptor
 *   - Custom "online-restored" event dispatched on the next successful request
 */
export default function OfflineBanner() {
  const [state, setState] = useState({
    offline: typeof navigator !== "undefined" && navigator.onLine === false,
    savedAt: null,
  });

  useEffect(() => {
    const onOffline = () => setState((s) => ({ ...s, offline: true }));
    const onOnline = () => setState({ offline: false, savedAt: null });
    const onFallback = (e) =>
      setState({ offline: true, savedAt: e.detail?.savedAt || null });
    const onRestored = () => setState({ offline: false, savedAt: null });

    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline-fallback", onFallback);
    window.addEventListener("online-restored", onRestored);
    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline-fallback", onFallback);
      window.removeEventListener("online-restored", onRestored);
    };
  }, []);

  if (!state.offline) return null;

  const savedText = state.savedAt
    ? new Date(state.savedAt).toLocaleString("es-ES", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <div
      role="status"
      data-testid="offline-banner"
      className="sticky top-0 z-40 w-full bg-amber-500 text-white px-3 py-2 text-sm flex items-center justify-center gap-2 shadow"
    >
      <WifiOff className="w-4 h-4 shrink-0" />
      <span className="font-semibold">Sin conexión</span>
      {savedText && (
        <span className="opacity-90 text-xs">· datos guardados el {savedText}</span>
      )}
      <button
        onClick={() => window.location.reload()}
        className="ml-2 inline-flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 rounded px-2 py-0.5"
        data-testid="offline-banner-retry"
      >
        <RefreshCw className="w-3 h-3" /> Reintentar
      </button>
    </div>
  );
}
